import { PipelineGroupingValidation } from "@/types/contract-family-pipeline";

const fallbackPipelineError = "The workflow could not continue. Review the current stage and try again.";
const maximumInvalidFamilyDetails = 50;

const pipelineErrorMessages: Record<string, string> = {
  "Original collection name is required": "Enter a name for the source collection before continuing.",
  "Original collection name is too long": "The source collection name must be 255 characters or fewer.",
  "Grouping job not found": "The grouping job is no longer available. Return to the folder and start grouping again.",
  "Pipeline run not found": "The workflow run is no longer available. Return to the folder and start again.",
  "Original collection not found": "The source collection is no longer available. Restart the workflow.",
  "Organization administrator permission required":
    "Organization administrator permission is required for this action.",
  contract_family_configuration_required:
    "The Contract Family settings are incomplete: the Snapshot Template, Master Collection or Publisher is not set or no longer available. An organization administrator can fix this in Contract Family settings.",
  base_collection_required:
    "An organization administrator must select the Supplier Base Collection in the Contract Family configuration first.",
  family_collection_not_found:
    "The selected Master Collection is no longer compatible or available. Refresh the configuration and try again.",
  summary_batch_model_not_batch_capable:
    "The configured summary model does not support Batch API execution. Choose a batch-capable model or use synchronous mode.",
  summary_batch_permanent_failures:
    "Some summary units have permanent failures. Review those failures before retrying the remaining units.",
  exactly_one_master:
    "Each family must contain exactly one MASTER document. Review the document roles, set one MASTER per family, then try again.",
  "Pipeline run is not awaiting review":
    "This workflow is no longer awaiting review. Refresh the page to see its current stage.",
  "Summary prompts are not configured": "Configure at least one summary prompt before generating summaries.",
  "Original collection extraction is incomplete":
    "Document extraction is still in progress. Wait for it to finish, then try again.",
  "Template not found": "The selected Snapshot template is no longer available. Select another active template.",
  "Pipeline prompts cannot be changed from the current stage":
    "Summary prompts cannot be changed at this workflow stage. Refresh the page to see its current state.",
  "At least one field prompt is required": "Configure at least one summary prompt before continuing.",
  "Summary prompts exceed the allowed size": "The summary prompts are too large. Shorten them and try again.",
  "Pipeline run requires a confirmed grouping": "Confirm the contract-family grouping before starting extraction.",
  "Pipeline run is not ready to materialise":
    "The final collection is not ready yet. Complete extraction and summary generation first.",
  "Fixed template has no fields":
    "The selected Snapshot template has no fields. Add at least one field or select another template.",
  "Family summaries are incomplete":
    "Some family summaries are still missing. Generate all summaries before creating the final collection.",
  "Summary field no longer belongs to template":
    "The Snapshot template changed during processing. Restart the workflow with the current template.",
  "prompts_by_field_id must not be empty": "Configure at least one summary prompt before continuing.",
  "Pipeline extraction is not ready": "Start and complete document extraction before generating summaries.",
  "Pipeline run cannot generate summaries from its current stage":
    "Summaries cannot be generated at this workflow stage. Refresh the page to see its current state.",
  "Every prompt field must belong to the fixed template":
    "One or more summary fields no longer belong to the selected template. Refresh the page and review the fields.",
  summary_generation_failed: "Family summary generation failed. Review the source documents and try again.",
  no_source_values:
    "No extracted source values were available for a family summary. Review the source documents and extraction results.",
  generation_failed: "A family summary could not be generated. Review the source documents and try again.",
  invalid_collection_name: "Enter a source collection name of 255 characters or fewer.",
  invalid_family_collection_name: "Enter a Master Collection name of 255 characters or fewer.",
  invalid_stage_transition: "The workflow stage changed. Refresh the page and try again.",
  invalid_document_selection: "One or more selected documents can no longer be changed. Refresh and try again.",
  cannot_exclude_all_documents: "At least one successfully processed document must remain in the workflow.",
  snapshot_orchestration_failed: "Snapshot generation could not be started. Refresh the workflow and try again.",
  template_has_no_fields: "The selected Snapshot template has no fields. Select another template.",
  snapshots_not_ready: "Snapshots are still processing or require review before categorisation.",
  pipeline_scope_not_visible: "One or more workflow documents are no longer available to your account.",
  grouping_scope_mismatch: "The grouping result no longer matches this workflow. Restart categorisation.",
  summary_in_progress: "Wait for the active summary attempt to finish before editing the grouping.",
  staged_values_not_ready: "Some summary rows are not ready for publication. Wait for processing to finish.",
  staged_value_invalid: "Review the staged summary values and complete every required field.",
  family_collection_conflict:
    "The Family Collection changed after this review. Refresh and review the latest approved rows.",
  publication_already_exists: "This workflow has already been published with a different request.",
  idempotency_key_reused: "The publication retry key is no longer valid. Refresh the workflow and try again.",
  run_in_progress: "A contract summary is already being generated for this family.",
  summary_prompts_not_configured: "Contract summary prompts are not configured for your organisation.",
  collection_template_mismatch: "This collection was not created from the supplier template.",
  empty_collection: "This collection has no completed snapshots to summarise.",
  contract_summary_run_in_progress:
    "A contract summary is being generated from this collection. Try again when it has finished.",
  snapshots_failed:
    "Some documents could not be processed, so the contract summary was not created. Check the documents in the folder and try again.",
  documents_not_fully_visible:
    "Some documents of this contract family are not visible to your account, so the summary would be incomplete. Ask someone with access to all of its documents to create it.",
  standalone_summary_staging_failed: "The documents could not be prepared for the summary. Try again.",
  snapshots_in_progress:
    "Snapshots in this collection are still being created. Wait for them to finish, then try again.",
  summary_stalled: "Summary generation stopped making progress and was ended. Try again.",
  automation_timed_out: "The contract summary took too long and was ended. Try again.",
  automation_dispatch_failed: "The contract summary could not be started. Try again in a moment.",
  cancelled: "The contract summary was cancelled.",
  pipeline_authorization_failed:
    "The contract summary was stopped because the folder, the collection or the settings it relies on are no longer available to the person who started it.",
  summary_prompts_missing: "Contract summary prompts are not configured for the Snapshot Template.",
  folder_grouping_not_configured: "Contract summaries by folder are not available on this environment.",
  pipeline_failed: "The contract summary could not be generated. Try again.",
  family_version_conflict:
    "Another contract summary for the same family was published at the same time. Try again to include the latest version.",
  empty_folder: "This folder has no documents to summarise.",
  too_many_documents: "There are too many documents to summarise at once. Summarise a smaller selection.",
  folder_run_not_editable:
    "This contract summary groups documents by folder, so its grouping cannot be categorised or edited.",
  family_collection_read_only:
    "A Master Collection only holds published contract summaries. Snapshots cannot be added to it, and it cannot be shared with the whole organization: share it with individual users instead.",
  base_collection_in_use: "Contract summaries were built from this collection, so it cannot be deleted.",
  family_collection_not_cancellable: "A Master Collection holds published contract summaries and cannot be cancelled.",
  family_collection_in_use:
    "This Master Collection is used by the Contract Family settings or holds published contract summaries, so it cannot be deleted. Choose another Master Collection in Contract Family settings first.",
  pipeline_managed_collection:
    "This collection is managed by the Contract Families workflow and cannot be edited here.",
};

const httpStatusMessages: Record<number, string> = {
  401: "Your session is no longer valid. Sign in again and retry the workflow.",
  403: "You do not have permission to perform this workflow action.",
  404: "The requested workflow resource is no longer available. Refresh the page and start again if needed.",
  409: "The workflow state changed before this action completed. Refresh the page and try again.",
  422: "Some workflow values are invalid. Review the current form and try again.",
  429: "Too many workflow requests were sent. Wait a moment and try again.",
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

export const parsePipelineGroupingValidation = (error: unknown): PipelineGroupingValidation | null => {
  if (!isRecord(error) || !isRecord(error.data) || error.data.detail !== "exactly_one_master") return null;
  const validation = error.data.validation;
  if (!isRecord(validation) || validation.code !== "exactly_one_master" || !Array.isArray(validation.families)) {
    return null;
  }
  if (validation.families.length > maximumInvalidFamilyDetails) return null;
  if (!Number.isInteger(validation.total_count) || (validation.total_count as number) < validation.families.length) {
    return null;
  }

  const families = validation.families;
  if (
    !families.every(
      family =>
        isRecord(family) &&
        typeof family.family_id === "string" &&
        family.family_id.length > 0 &&
        family.family_id.length <= 128 &&
        typeof family.name === "string" &&
        family.name.length > 0 &&
        family.name.length <= 255 &&
        Number.isInteger(family.master_count) &&
        (family.master_count as number) >= 0,
    )
  ) {
    return null;
  }

  return {
    code: "exactly_one_master",
    families: families as PipelineGroupingValidation["families"],
    total_count: validation.total_count as number,
  };
};

const contractSummaryFailureFallback = "The contract summary could not be generated.";

/** The reason an automated contract summary run failed, from its stored `error_code`. */
export const getContractSummaryFailureMessage = (errorCode: string | null | undefined): string =>
  (errorCode ? pipelineErrorMessages[errorCode] : undefined) ?? contractSummaryFailureFallback;

/** Refusals that protect a collection used by contract summaries; each has its own explanation. */
const collectionLockCodes = new Set([
  "family_collection_in_use",
  "family_collection_not_cancellable",
  "family_collection_read_only",
  "base_collection_in_use",
  "pipeline_managed_collection",
  "contract_summary_run_in_progress",
]);

/** Explains a refused collection or snapshot action when the reason is a contract summary lock. */
export const getCollectionLockMessage = (error: unknown, fallback: string): string => {
  const detail = isRecord(error) && isRecord(error.data) ? error.data.detail : undefined;
  return typeof detail === "string" && collectionLockCodes.has(detail) ? pipelineErrorMessages[detail] : fallback;
};

export const getPipelineErrorMessage = (error: unknown): string => {
  if (typeof error === "string") return pipelineErrorMessages[error] ?? fallbackPipelineError;
  if (typeof error !== "object" || error === null) return fallbackPipelineError;

  if ("data" in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === "object" && data !== null && "detail" in data) {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === "string" && detail in pipelineErrorMessages) return pipelineErrorMessages[detail];
    }
  }

  const status = "status" in error ? (error as { status?: unknown }).status : undefined;
  return typeof status === "number" ? (httpStatusMessages[status] ?? fallbackPipelineError) : fallbackPipelineError;
};
